import type {
  Agency,
  AgencyCategory,
  AgencyListTab,
} from '@saaga/shared/interfaces/agencies.interface'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { orgRoutes } from '@/routes/org-routes.const'

import { getTabFromPath } from './agency-tab.util'
import { useAgenciesData } from './use-agencies-data.hook'

export interface UseAgenciesFilterReturn {
  activeTab: AgencyListTab
  selectedCategoryList: AgencyCategory[]
  filteredAgencyList: Agency[]
  pinnedAgencyIdList: string[]
  isLoading: boolean
  isError: boolean
  handleTabChange: (tab: AgencyListTab) => void
  handleCategoryToggle: (category: AgencyCategory) => void
  handleClearCategoryFilter: () => void
  handlePin: (agency: Agency) => void
}

export const useAgenciesFilter = (): UseAgenciesFilterReturn => {
  const navigate = useNavigate()
  const location = useLocation()
  const orgSlug = useOrgSlug()

  const activeTab = getTabFromPath(location.pathname)
  const [selectedCategory, setSelectedCategory] = useState<AgencyCategory | null>(null)
  const [pinnedAgencyIdList, setPinnedAgencyIdList] = useState<string[]>([])

  const { agencyList, isLoading, isError } = useAgenciesData(
    activeTab,
    selectedCategory ?? undefined
  )

  const selectedCategoryList: AgencyCategory[] = selectedCategory ? [selectedCategory] : []

  const handleTabChange = useCallback(
    (tab: AgencyListTab): void => {
      navigate(`/${orgSlug}/${orgRoutes.agencies}/${tab}`)
      setSelectedCategory(null)
    },
    [navigate, orgSlug]
  )

  const handleCategoryToggle = useCallback((category: AgencyCategory): void => {
    setSelectedCategory((prev) => (prev === category ? null : category))
  }, [])

  const handleClearCategoryFilter = useCallback((): void => {
    setSelectedCategory(null)
  }, [])

  const handlePin = useCallback((agency: Agency): void => {
    setPinnedAgencyIdList((prev) =>
      prev.includes(agency.id) ? prev.filter((id) => id !== agency.id) : [...prev, agency.id]
    )
  }, [])

  return {
    activeTab,
    selectedCategoryList,
    filteredAgencyList: agencyList,
    pinnedAgencyIdList,
    isLoading,
    isError,
    handleTabChange,
    handleCategoryToggle,
    handleClearCategoryFilter,
    handlePin,
  }
}
